import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO/SEO';
import Reveal from '../../components/Reveal/Reveal';
import ArrowIcon from '../../components/icons/ArrowIcon';
import {
  IconRetail, IconFinance, IconLogistics, IconHealth,
  IconRealEstate, IconEducation, IconTarget, IconUsers, IconShield,
} from '../../components/icons/ServiceIcons';
import './Industries.css';

const PUBLIC_URL = process.env.PUBLIC_URL || '';
const HERO_BG = { '--hero-bg': `url(${PUBLIC_URL}/assets/hero-banner.png)` };
const CTA_BG = { '--cta-bg': `url(${PUBLIC_URL}/assets/cta-banner.png)` };

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const INDUSTRIES = [
  {
    id: 'retail',
    icon: <IconRetail />,
    title: 'Retail & E-Commerce',
    short: 'Catalogs, orders, inventory, and customer-facing storefronts.',
    description:
      'We help retail and online sellers build tools that make selling and fulfillment easier — from product catalogs to order flows and basic inventory views.',
    builds: [
      'Product catalogs and storefronts',
      'Order and checkout flows',
      'Inventory and stock views',
      'Admin panels for catalog and orders',
      'Payment gateway integrations',
    ],
  },
  {
    id: 'finance',
    icon: <IconFinance />,
    title: 'Finance & Operations',
    short: 'Dashboards, workflows, and tools that support day-to-day business operations.',
    description:
      'For finance and operations teams, we build practical software for reporting, approvals, and internal workflows — focused on clarity and control, not buzzwords.',
    builds: [
      'Operations dashboards',
      'Approval and workflow tools',
      'Internal reporting views',
      'Role-based access for teams',
      'Integrations with existing business systems',
    ],
  },
  {
    id: 'logistics',
    icon: <IconLogistics />,
    title: 'Logistics & Field Work',
    short: 'Tracking, dispatch-style apps, and mobile tools for on-ground teams.',
    description:
      'We build apps that help field and logistics teams stay coordinated — assignments, status updates, and mobile-friendly tools for work outside the office.',
    builds: [
      'Dispatch and assignment views',
      'Status tracking for jobs or shipments',
      'Mobile tools for field staff',
      'Simple maps and location-aware flows',
      'Ops dashboards for supervisors',
    ],
  },
  {
    id: 'healthcare',
    icon: <IconHealth />,
    title: 'Healthcare',
    short: 'Scheduling, records-oriented apps, and internal clinic workflows.',
    description:
      'We support clinics and healthcare teams with scheduling, internal workflows, and records-oriented applications designed carefully around privacy and access control.',
    builds: [
      'Appointment scheduling',
      'Clinic and staff admin tools',
      'Patient or member portals',
      'Internal records-oriented workflows',
      'Secure role-based access',
    ],
  },
  {
    id: 'real-estate',
    icon: <IconRealEstate />,
    title: 'Real Estate',
    short: 'Listings, lead management, and property-related portals.',
    description:
      'Property businesses need clear listings, lead capture, and follow-up tools. We build portals and CRMs that help agents and teams manage enquiries and inventory.',
    builds: [
      'Property listing portals',
      'Lead capture and enquiry forms',
      'Agent and team CRM views',
      'Property inventory management',
      'Client-facing property details pages',
    ],
  },
  {
    id: 'education',
    icon: <IconEducation />,
    title: 'Education',
    short: 'Learning portals, admissions flows, and admin tools for institutes.',
    description:
      'Institutes and training providers need portals for admissions, courses, and administration. We build practical education software that staff and students can actually use.',
    builds: [
      'Learning and course portals',
      'Admissions and enquiry flows',
      'Student or member dashboards',
      'Admin tools for institutes',
      'Assessment and progress views',
    ],
  },
];

const APPROACH = [
  {
    icon: <IconTarget />,
    title: 'Start from the workflow',
    text: 'We learn how your team works today, then design software around real tasks — not generic industry templates.',
  },
  {
    icon: <IconUsers />,
    title: 'Build for the people using it',
    text: 'Operators, field staff, admins, and customers each need different screens. We keep those paths clear.',
  },
  {
    icon: <IconShield />,
    title: 'Keep access and data sensible',
    text: 'Role-based access and careful handling of business data from the start, especially for sensitive domains.',
  },
];

const Industries = () => (
  <div className="industries-page">
    <SEO
      title="Industries We Serve | Retail, Finance, Healthcare, Education | CodeInventiv"
      description="Software for retail, finance & operations, logistics, healthcare, real estate, and education. CodeInventiv Solutions builds practical tools for teams across different industries."
      keywords="software for retail, finance operations software, logistics apps, healthcare software, real estate portals, education portals, software development Jaipur"
      ogType="website"
    />

    <section className="page-hero" style={HERO_BG}>
      <div className="container page-hero-inner">
        <Reveal>
          <div className="section-eyebrow on-dark">Industries</div>
          <h1 className="page-hero-title">
            Software for teams across<br />
            <em>different industries</em>
          </h1>
          <p className="page-hero-sub">
            We build practical tools for operations, customers, and internal teams. Here are the
            industries we commonly work with — and the kinds of products we deliver in each.
          </p>
          <div className="page-hero-points">
            <span><CheckIcon /> Domain-aware delivery</span>
            <span><CheckIcon /> Built around real workflows</span>
            <span><CheckIcon /> Clear scope and communication</span>
          </div>
          <div className="page-hero-actions">
            <Link to="/contact" className="btn btn-gold">
              Discuss Your Industry
              <ArrowIcon />
            </Link>
            <a href="#industry-list" className="btn btn-ghost-light">Browse Industries</a>
          </div>
        </Reveal>
      </div>
    </section>

    <section className="section section-light" id="industry-list">
      <div className="container">
        <Reveal className="section-head">
          <div className="section-eyebrow">Overview</div>
          <h2 className="section-title">Industries where we build <em>useful software</em></h2>
          <p className="section-sub">
            Not every business needs the same product. These are common contexts we understand and
            can start from quickly.
          </p>
        </Reveal>
        <div className="industries-overview-grid">
          {INDUSTRIES.map((s) => (
            <Reveal key={s.id} as="a" href={`#${s.id}`} className="industries-overview-card">
              <div className="industries-overview-icon">{s.icon}</div>
              <h3>{s.title}</h3>
              <p>{s.short}</p>
              <span className="industries-overview-link">
                Learn more
                <ArrowIcon size={14} />
              </span>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="section section-pale" id="industry-details">
      <div className="container">
        <Reveal className="section-head">
          <div className="section-eyebrow">What We Build</div>
          <h2 className="section-title">Examples of software in <em>each industry</em></h2>
        </Reveal>
        <div className="industries-detail-list">
          {INDUSTRIES.map((s, index) => (
            <Reveal
              key={s.id}
              as="article"
              id={s.id}
              className={`industries-detail${index % 2 === 1 ? ' reverse' : ''}`}
            >
              <div className="industries-detail-copy">
                <div className="industries-detail-icon">{s.icon}</div>
                <h3>{s.title}</h3>
                <p className="industries-detail-lead">{s.description}</p>
                <ul className="industries-detail-features">
                  {s.builds.map((item) => (
                    <li key={item}><CheckIcon />{item}</li>
                  ))}
                </ul>
                <Link to="/contact" className="btn btn-gold">
                  Talk About a Project
                  <ArrowIcon />
                </Link>
              </div>
              <div className="industries-detail-panel">
                <div className="industries-detail-panel-label">Typical deliverables</div>
                <ol>
                  {s.builds.slice(0, 4).map((item, i) => (
                    <li key={item}>
                      <span>{String(i + 1).padStart(2, '0')}</span>
                      {item}
                    </li>
                  ))}
                </ol>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="section section-light" id="approach">
      <div className="container">
        <Reveal className="section-head">
          <div className="section-eyebrow">Our Approach</div>
          <h2 className="section-title">How we work across <em>different domains</em></h2>
          <p className="section-sub">
            Industry experience helps us ask better questions. Delivery still starts with your
            specific workflows and constraints.
          </p>
        </Reveal>
        <div className="industries-approach-grid">
          {APPROACH.map((item) => (
            <Reveal key={item.title} as="article" className="industries-approach-card">
              <div className="industries-approach-icon">{item.icon}</div>
              <h3>{item.title}</h3>
              <p>{item.text}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="cta-band" style={CTA_BG}>
      <div className="container cta-inner">
        <Reveal className="cta-copy">
          <h2>Don’t see your industry listed? <em>That’s fine.</em></h2>
          <p>
            Tell us how your team works and what you need to build. If it fits our strengths, we will
            say so clearly — and suggest a practical starting point.
          </p>
        </Reveal>
        <Reveal className="cta-actions">
          <Link to="/contact" className="btn btn-gold">
            Get a Quote
            <ArrowIcon />
          </Link>
          <Link to="/services" className="btn btn-ghost-light">View Services</Link>
        </Reveal>
      </div>
    </section>
  </div>
);

export default Industries;
